import React from "react";
import PropTypes from "prop-types";
import { App, Button, Space, Spin, Switch, Tag, Tooltip } from "antd";
import { DeleteOutlined, ExclamationCircleFilled } from "@ant-design/icons";

import AntTable from "components/UI/AntTable";
import DashedTooltip from "components/UI/DashedTooltip";
import GenericCard from "components/UI/GenericCard/GenericCard";
import { formatMoney } from "./optionTradeUtils";
import {
  apiDeleteOptionStrategy,
  apiUpdateOptionStrategy,
} from "api/apiOptionStrategies";
import { returnUpDownClassname } from "utils/helpers";
import {
  describeSizing,
  describeWindow,
  formatDay,
  formatIv,
  formatPercent,
  formatPrice,
} from "./optionStrategyUtils";

const formatExpiration = (expiration) =>
  `${expiration.slice(0, 4)}-${expiration.slice(4, 6)}-${expiration.slice(6, 8)}`;

const OptionsStrategiesTable = (props) => {
  const {
    strategies,
    isLoading,
    loadedIds,
    snapshots,
    onLoad,
    onUnload,
    onAnalyze,
    onChanged,
  } = props;
  const { message, modal } = App.useApp();

  const onToggleEnabled = async (strategy, enabled) => {
    try {
      await apiUpdateOptionStrategy(strategy._id, { enabled });
      onChanged();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update");
    }
  };

  const onDelete = (strategy) => {
    modal.confirm({
      title: `Delete "${strategy.name}"?`,
      icon: <ExclamationCircleFilled />,
      content:
        "This permanently deletes the strategy with all its trades and activity.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          await apiDeleteOptionStrategy(strategy._id);
          onUnload(strategy._id);
          message.success("Strategy deleted");
          onChanged();
        } catch (err) {
          message.error(err?.response?.data?.message || "Failed to delete");
        }
      },
    });
  };

  const renderSnapshot = (row, render) => {
    const snapshot = snapshots[row._id];
    if (!snapshot || snapshot.isLoading) return <Spin size="small" />;
    if (snapshot.error) {
      return (
        <Tooltip title={snapshot.error}>
          <span style={{ color: "#999" }}>n/a</span>
        </Tooltip>
      );
    }
    return render(snapshot.data);
  };

  const columns = [
    {
      title: "",
      key: "load",
      width: 90,
      render: (row) =>
        loadedIds.includes(row._id) ? (
          <Button size="small" onClick={() => onUnload(row._id)}>
            Unload
          </Button>
        ) : (
          <Button size="small" type="primary" onClick={() => onLoad(row)}>
            Load
          </Button>
        ),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: { compare: (a, b) => a.name.localeCompare(b.name) },
    },
    {
      title: "Symbol",
      dataIndex: "symbol",
      sorter: { compare: (a, b) => a.symbol.localeCompare(b.symbol) },
    },
    {
      title: "Model",
      key: "model",
      render: (row) => row.models?.map((m) => m.name).join(", ") || "-",
    },
    {
      title: "Mode",
      key: "mode",
      render: (row) =>
        row.signalsOnly ? (
          <Tag>Signals only</Tag>
        ) : row.isDemo ? (
          <Tag color="red">Demo</Tag>
        ) : (
          <Tag color="green">Real</Tag>
        ),
    },
    {
      title: (
        <DashedTooltip text="Prediction from the 15:50 ET early run">
          Early signal
        </DashedTooltip>
      ),
      key: "signal",
      render: (row) =>
        row.lastSignal?.date ? (
          <>
            <Tag
              color={row.lastSignal.prediction === "buy" ? "green" : "default"}
            >
              {row.lastSignal.prediction}
            </Tag>
            {formatDay(row.lastSignal.date)}
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Take profit",
      key: "tp",
      render: (row) =>
        row.params?.takeProfitPercent != null
          ? `${row.params.takeProfitPercent}%`
          : "-",
    },
    {
      title: (
        <DashedTooltip text="Trading bars to the option's expiry allowed for an entry">
          Expiry window
        </DashedTooltip>
      ),
      key: "window",
      render: (row) => describeWindow(row.params),
    },
    {
      title: "Size",
      key: "size",
      render: (row) =>
        row.signalsOnly
          ? "-"
          : `${describeSizing(row.params)} · max ${row.params?.maxOpenOrders ?? 1}`,
    },
    {
      title: "Equity",
      key: "equity",
      render: (row) => (row.signalsOnly ? "-" : `$${formatMoney(row.equity)}`),
    },
    {
      title: "Net P&L",
      key: "pnl",
      render: (row) =>
        row.signalsOnly ? (
          "-"
        ) : (
          <span className={returnUpDownClassname(row.metrics?.pnl)}>
            ${formatMoney(row.metrics?.pnl)}
          </span>
        ),
      sorter: {
        compare: (a, b) => (a.metrics?.pnl || 0) - (b.metrics?.pnl || 0),
      },
    },
    {
      title: "Trades",
      key: "trades",
      render: (row) => (
        <Tooltip
          title={`${row.metrics?.successRate ?? 0}% success · ${row.openPositions?.length || 0} open · ${row.pendingBuys?.length || 0} buy signals`}
        >
          {row.metrics?.totalTransactions || 0} closed /{" "}
          {row.openPositions?.length || 0} open
          {row.pendingBuys?.length > 0 && (
            <Tag color="lime" style={{ marginLeft: 6 }}>
              {row.pendingBuys.length} signal
            </Tag>
          )}
        </Tooltip>
      ),
    },
    {
      title: (
        <DashedTooltip text="Live ATM call now (nearest expiry at least min bars away), from IBKR">
          Live ATM call
        </DashedTooltip>
      ),
      key: "atm",
      render: (row) =>
        renderSnapshot(row, (data) => (
          <Tooltip
            title={`Strike ${data.strike} · expires ${formatExpiration(data.expiration)} (${data.tradingDaysToExpiration}d) · IV ${formatIv(data.iv)}`}
          >
            {formatPrice(data.bid)} / {formatPrice(data.ask)}
          </Tooltip>
        )),
    },
    {
      title: (
        <DashedTooltip text="Live ATM call return if the underlying reaches the take profit: hit next day / only at expiration">
          Profit range
        </DashedTooltip>
      ),
      key: "range",
      render: (row) =>
        renderSnapshot(row, (data) =>
          data.profitUnavailableReason ? (
            <Tooltip title={data.profitUnavailableReason}>
              <span style={{ color: "#999" }}>n/a</span>
            </Tooltip>
          ) : (
            <>
              <span
                className={returnUpDownClassname(data.bestCase?.profitPercent)}
              >
                {formatPercent(data.bestCase?.profitPercent, 0)}
              </span>
              {" / "}
              <span
                className={returnUpDownClassname(data.worstCase?.profitPercent)}
              >
                {formatPercent(data.worstCase?.profitPercent, 0)}
              </span>
            </>
          ),
        ),
    },
    {
      title: "Enabled",
      key: "enabled",
      render: (row) => (
        <Switch
          size="small"
          checked={row.enabled}
          onChange={(checked) => onToggleEnabled(row, checked)}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      render: (row) => (
        <Space>
          <Button size="small" onClick={() => onAnalyze(row)}>
            Analyze
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(row)}
          />
        </Space>
      ),
    },
  ];

  return (
    <GenericCard
      title={`Options Strategies (${strategies.length})`}
      className="table-card"
    >
      <AntTable
        rowKey="_id"
        columns={columns}
        dataSource={strategies}
        loading={isLoading}
        virtual={false}
        rowClassName={(row) =>
          loadedIds.includes(row._id) ? "table-row-selected" : ""
        }
      />
    </GenericCard>
  );
};

OptionsStrategiesTable.propTypes = {
  strategies: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  loadedIds: PropTypes.array.isRequired,
  snapshots: PropTypes.object.isRequired,
  onLoad: PropTypes.func.isRequired,
  onUnload: PropTypes.func.isRequired,
  onAnalyze: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default OptionsStrategiesTable;
